import React, { useMemo, useState } from 'react';
import Icon from '../AppIcon';

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function startOfCalendarGrid(year, monthIndex) {
  const first = new Date(year, monthIndex, 1);
  const dow = first.getDay();
  const offset = dow === 0 ? 6 : dow - 1;
  const start = new Date(year, monthIndex, 1 - offset);
  start.setHours(0, 0, 0, 0);
  return start;
}

export default function CallbackRemindersCalendar({
  reminders = [],
  onProspectClick,
  className = '',
}) {
  const now = new Date();
  const year = now.getFullYear();
  const monthIndex = now.getMonth();
  const [selectedDay, setSelectedDay] = useState(null);

  const byDayKey = useMemo(() => {
    const map = new Map();
    reminders.forEach((r) => {
      const d = r.callbackAt;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return map;
  }, [reminders]);

  const cells = useMemo(() => {
    const start = startOfCalendarGrid(year, monthIndex);
    const list = [];
    for (let i = 0; i < 42; i += 1) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      const inMonth = date.getMonth() === monthIndex;
      const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      list.push({
        date,
        inMonth,
        key,
        items: byDayKey.get(key) || [],
      });
    }
    return list;
  }, [year, monthIndex, byDayKey]);

  const monthLabel = now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  const selectedItems = useMemo(() => {
    if (!selectedDay) return [];
    const key = `${selectedDay.getFullYear()}-${selectedDay.getMonth()}-${selectedDay.getDate()}`;
    return byDayKey.get(key) || [];
  }, [selectedDay, byDayKey]);

  const upcoming = useMemo(
    () =>
      [...reminders]
        .filter((r) => r.callbackAt >= new Date(now.getFullYear(), now.getMonth(), now.getDate()))
        .slice(0, 5),
    [reminders, now],
  );

  return (
    <div
      className={`rounded-2xl border border-border/80 bg-card p-4 shadow-sm md:p-5 ${className}`}
    >
      <div className="mb-4 flex items-start justify-between gap-2">
        <div>
          <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
            <Icon name="Calendar" size={18} className="text-primary" />
            Rappels du mois
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground capitalize">{monthLabel}</p>
          <p className="text-xs text-muted-foreground">Uniquement les prospects qui vous sont assignés.</p>
        </div>
        <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
          {reminders.length} rappel{reminders.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="mb-2 grid grid-cols-7 gap-1 text-center text-[10px] font-medium uppercase text-muted-foreground">
        {WEEKDAYS.map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map(({ date, inMonth, key, items }) => {
          const isToday = sameDay(date, now);
          const isSelected = selectedDay && sameDay(date, selectedDay);
          const hasItems = items.length > 0;
          return (
            <button
              key={key}
              type="button"
              disabled={!inMonth}
              onClick={() => inMonth && setSelectedDay(new Date(date))}
              className={`relative flex min-h-[44px] flex-col items-center justify-start rounded-lg border px-0.5 py-1 text-xs transition-colors ${
                !inMonth
                  ? 'cursor-default border-transparent text-muted-foreground/40'
                  : isSelected
                    ? 'border-primary bg-primary/10 text-foreground'
                    : isToday
                      ? 'border-primary/40 bg-muted/50 text-foreground'
                      : 'border-transparent hover:bg-muted/60 text-foreground'
              }`}
            >
              <span className={`font-medium ${isToday ? 'text-primary' : ''}`}>
                {date.getDate()}
              </span>
              {inMonth && hasItems ? (
                <span className="mt-0.5 flex gap-0.5">
                  {items.length <= 3
                    ? items.map((_, i) => (
                        <span
                          key={i}
                          className="h-1.5 w-1.5 rounded-full bg-warning"
                        />
                      ))
                    : (
                        <span className="text-[9px] font-semibold text-warning">
                          +{items.length}
                        </span>
                      )}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="mt-4 border-t border-border pt-4">
        {selectedDay ? (
          <>
            <p className="mb-2 text-xs font-medium text-foreground">
              {selectedDay.toLocaleDateString('fr-FR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
              })}
            </p>
            {selectedItems.length === 0 ? (
              <p className="text-xs text-muted-foreground">Aucun rappel ce jour-là.</p>
            ) : (
              <ul className="space-y-2">
                {selectedItems.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => onProspectClick?.(r.prospectId)}
                      className="flex w-full items-center justify-between gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-left text-sm hover:bg-muted/60"
                    >
                      <span className="min-w-0 truncate font-medium text-foreground">
                        {r.fullName}
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {r.callbackAt.toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <>
            <p className="mb-2 text-xs font-medium text-muted-foreground">Prochains rappels</p>
            {upcoming.length === 0 ? (
              <p className="text-xs text-muted-foreground">Aucun rappel planifié ce mois-ci.</p>
            ) : (
              <ul className="space-y-2">
                {upcoming.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => onProspectClick?.(r.prospectId)}
                      className="flex w-full items-center gap-3 rounded-lg border border-border px-3 py-2 text-left text-sm hover:bg-muted/40"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-warning/15 text-warning">
                        <Icon name="PhoneForwarded" size={14} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-foreground">
                          {r.fullName}
                        </span>
                        {r.company ? (
                          <span className="block truncate text-xs text-muted-foreground">
                            {r.company}
                          </span>
                        ) : null}
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {r.callbackAt.toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                        })}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}
